import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Loader2, Lock, Smartphone } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/PublicShell";

type Stage = { step: "creds" } | { step: "mfa_verify"; mfa_token: string } | { step: "mfa_setup"; mfa_token: string; qr: string; secret: string };

export default function AdminLogin() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const from = (useLocation().state as any)?.from;
  const dest = typeof from === "string" && /^\/admin(\/|$)/.test(from) && !from.startsWith("/admin/login") ? from : "/admin";
  const [stage, setStage] = useState<Stage>({ step: "creds" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to={dest} replace />;

  const submitCreds = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.step === "done") {
        setUser(data.user);
        navigate(dest);
        return;
      }
      setStage(data);
    } catch (err) {
      setError(errMsg(err));
    }
    setBusy(false);
  };

  const submitMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (stage.step === "creds") return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/mfa/verify", { mfa_token: stage.mfa_token, code });
      setUser(data);
      navigate(dest);
    } catch (err) {
      setError(errMsg(err));
    }
    setBusy(false);
  };

  return (
    <div className="grid min-h-screen bg-[#F9F8F5] lg:grid-cols-[1fr_520px]">
      <div className="relative hidden overflow-hidden bg-[#1B365D] p-12 lg:flex lg:flex-col lg:justify-between">
        <BrandMark light />
        <div className="max-w-md">
          <h1 className="text-4xl font-extrabold leading-tight text-white">Internal operations console</h1>
          <p className="mt-4 text-lg text-white/70">Authorized staff only. All access and record changes are logged.</p>
        </div>
        <div className="absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-[#F5C77E]/15 blur-3xl" />
        <p className="flex items-center gap-2 text-sm text-white/50"><Lock className="h-4 w-4" /> Secure, encrypted sign-in</p>
      </div>
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md fade-up">
          <div className="mb-8 lg:hidden"><BrandMark /></div>
          {stage.step === "creds" ? (
            <form onSubmit={submitCreds} className="space-y-5" data-testid="admin-login-form">
              <h2 className="text-3xl font-extrabold tracking-tight">Staff sign in</h2>
              <label className="block"><span className="field-label">Email</span><input data-testid="admin-login-email-input" type="email" required className="field-input" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" /></label>
              <label className="block"><span className="field-label">Password</span><input data-testid="admin-login-password-input" type="password" required className="field-input" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" /></label>
              {error && <p className="rounded-xl bg-[#FEE2E2] px-4 py-3 text-sm text-[#991B1B]" data-testid="admin-login-error">{error}</p>}
              <button data-testid="admin-login-submit-button" disabled={busy} className="btn-primary w-full">{busy && <Loader2 className="h-5 w-5 animate-spin" />} Continue</button>
            </form>
          ) : (
            <form onSubmit={submitMfa} className="space-y-5" data-testid="admin-mfa-form">
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[#1B365D] text-[#F5C77E]"><Smartphone className="h-6 w-6" /></span>
              <h2 className="text-3xl font-extrabold tracking-tight">{stage.step === "mfa_setup" ? "Set up two-step verification" : "Enter your 6-digit code"}</h2>
              {stage.step === "mfa_setup" && (
                <div className="card-soft p-5" data-testid="admin-mfa-setup-panel">
                  <p className="text-[15px] text-[#475569]">Scan this QR code with Google Authenticator, Authy, or another authenticator app.</p>
                  <img src={stage.qr} alt="MFA QR code" className="mx-auto my-4 h-48 w-48" data-testid="admin-mfa-qr" />
                  <p className="text-xs text-[#64748B]">Can't scan? Enter this key:</p>
                  <p className="mt-1 break-all rounded-lg bg-[#F1EFEA] px-3 py-2 font-mono text-sm" data-testid="admin-mfa-secret">{stage.secret}</p>
                </div>
              )}
              <input
                data-testid="admin-mfa-totp-input"
                inputMode="numeric"
                maxLength={6}
                required
                autoFocus
                className="field-input h-16 text-center font-mono text-3xl tracking-[0.5em]"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              />
              {error && <p className="rounded-xl bg-[#FEE2E2] px-4 py-3 text-sm text-[#991B1B]" data-testid="admin-login-error">{error}</p>}
              <button data-testid="admin-mfa-submit-button" disabled={busy || code.length !== 6} className="btn-primary w-full">{busy && <Loader2 className="h-5 w-5 animate-spin" />} Verify & sign in</button>
              <button type="button" className="w-full text-sm font-semibold text-[#64748B] hover:text-[#1B365D]" onClick={() => { setStage({ step: "creds" }); setCode(""); }} data-testid="admin-mfa-back">Use a different account</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
